import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import type { EChartsCoreOption } from 'echarts/core';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvLoadingItemComponent } from '../../../../Shared/Components/av-loading-item/av-loading-item.component';
import { ThemeService } from '../../../../Shared/Services/theme.service';
import { EntraAdminRolesReportFacade } from '../../../../../Facades/reports/entra-admin-roles.facade';
import { AvTableColumn, AvTableRow } from '../../../../Shared/Interfaces/av-table-card.interface';
import { exportAllToCsv } from '../../../../Shared/Utils/csv-export.utils';
import { ReportKpiTileComponent } from '../../Partials/report-kpi-tile/report-kpi-tile.component';
import { ReportChartCardComponent } from '../../Partials/report-chart-card/report-chart-card.component';
import { ReportDetailDialogComponent } from '../../Dialogs/report-detail-dialog/report-detail-dialog.component';
import { ReportDetailSection } from '../../../models/report-dashboard.types';
import {
  categoryAxis,
  CHART_ACCENT_ORANGE,
  CHART_ACCENT_PURPLE,
  donutSeriesDefaults,
  integerValueAxis,
  legendBottom,
  readChartThemePalette,
} from '../../../utils/chart-theme.utils';
import { RECENT_ACTIVITY_LIMIT } from '../../../Consts/recent-activity-limit.const';

@Component({
  selector: 'app-entra-admin-roles-report',
  standalone: true,
  imports: [COMMON_IMPORTS, AvLoadingItemComponent, ReportKpiTileComponent, ReportChartCardComponent],
  templateUrl: './entra-admin-roles-report.component.html',
  styleUrl: './entra-admin-roles-report.component.scss',
})
export class EntraAdminRolesReportComponent {
  readonly facade = inject(EntraAdminRolesReportFacade);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly themeService = inject(ThemeService);
  private readonly translationsReady = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly assignmentTypeDonutOptions = signal<EChartsCoreOption | null>(null);
  readonly roleBarOptions = signal<EChartsCoreOption | null>(null);

  private readonly userAssignmentColumns: AvTableColumn[] = [
    { key: 'principalDisplayName', label: 'app.reports.entra_admin_roles.columns.principalDisplayName', sortable: true },
    { key: 'userPrincipalName', label: 'app.reports.entra_admin_roles.columns.userPrincipalName', sortable: true },
    { key: 'accountStatus', label: 'app.reports.entra_admin_roles.columns.accountStatus', sortable: true },
    { key: 'roleName', label: 'app.reports.entra_admin_roles.columns.roleName', sortable: true },
    { key: 'roleScope', label: 'app.reports.entra_admin_roles.columns.roleScope', sortable: true },
  ];

  private readonly groupColumns: AvTableColumn[] = [
    { key: 'groupName', label: 'app.reports.entra_admin_roles.columns.groupName', sortable: true },
    { key: 'roleName', label: 'app.reports.entra_admin_roles.columns.roleName', sortable: true },
    { key: 'assignmentType', label: 'app.reports.entra_admin_roles.columns.assignmentType', sortable: true },
    { key: 'memberCount', label: 'app.reports.entra_admin_roles.columns.memberCount', sortable: true },
  ];

  private readonly servicePrincipalColumns: AvTableColumn[] = [
    { key: 'principalDisplayName', label: 'app.reports.entra_admin_roles.columns.principalDisplayName', sortable: true },
    { key: 'roleName', label: 'app.reports.entra_admin_roles.columns.roleName', sortable: true },
    { key: 'roleScope', label: 'app.reports.entra_admin_roles.columns.roleScope', sortable: true },
  ];

  private readonly pimAuditColumns: AvTableColumn[] = [
    { key: 'user', label: 'app.reports.entra_admin_roles.columns.user', sortable: true },
    { key: 'roleName', label: 'app.reports.entra_admin_roles.columns.roleName', sortable: true },
    { key: 'status', label: 'app.reports.entra_admin_roles.columns.status', sortable: true },
    { key: 'activityDateTime', label: 'app.reports.entra_admin_roles.columns.activityDateTime', sortable: true },
    { key: 'reason', label: 'app.reports.entra_admin_roles.columns.reason', sortable: true },
  ];

  private readonly permanentRows = computed<AvTableRow[]>(() =>
    this.facade.permanentUserAssignments().map(d => ({ ...d }))
  );

  private readonly eligibleRows = computed<AvTableRow[]>(() =>
    this.facade.eligibleUserAssignments().map(d => ({ ...d }))
  );

  private readonly groupRows = computed<AvTableRow[]>(() =>
    this.facade.groupAssignments().map(d => ({ ...d }))
  );

  private readonly servicePrincipalRows = computed<AvTableRow[]>(() =>
    this.facade.servicePrincipalAssignments().map(d => ({ ...d }))
  );

  private readonly pimAuditRows = computed<AvTableRow[]>(() =>
    this.facade.pimAuditLogs().map(d => ({ ...d }))
  );

  private readonly privilegedIdentityRows = computed<AvTableRow[]>(() =>
    this.facade.allUserAssignmentRows().map(d => ({ ...d }))
  );

  private readonly globalAdminRows = computed<AvTableRow[]>(() =>
    this.facade.globalAdminAssignmentRows().map(d => ({ ...d }))
  );

  private readonly disabledWithRolesRows = computed<AvTableRow[]>(() =>
    this.facade.disabledWithRoles().map(d => ({ ...d }))
  );

  readonly recentActivityFeed = computed(() => this.facade.activityFeed().slice(0, RECENT_ACTIVITY_LIMIT));

  readonly sections: ReportDetailSection[] = [
    { key: 'permanentUserAssignments', titleKey: 'app.reports.entra_admin_roles.permanent_assignments', icon: 'verified_user', columns: this.userAssignmentColumns, rows: () => this.permanentRows() },
    { key: 'eligibleUserAssignments', titleKey: 'app.reports.entra_admin_roles.eligible_assignments', icon: 'schedule', columns: this.userAssignmentColumns, rows: () => this.eligibleRows() },
    { key: 'groupAssignments', titleKey: 'app.reports.entra_admin_roles.group_assignments', icon: 'group', columns: this.groupColumns, rows: () => this.groupRows() },
    { key: 'servicePrincipalAssignments', titleKey: 'app.reports.entra_admin_roles.service_principal_assignments', icon: 'business', columns: this.servicePrincipalColumns, rows: () => this.servicePrincipalRows() },
    { key: 'pimAuditLogs', titleKey: 'app.reports.entra_admin_roles.pim_audit_logs', icon: 'history', columns: this.pimAuditColumns, rows: () => this.pimAuditRows() },
  ];

  private readonly privilegedIdentitiesSection: ReportDetailSection = {
    key: 'privilegedIdentities',
    titleKey: 'app.reports.dashboard.privileged_identities',
    icon: 'badge',
    columns: this.userAssignmentColumns,
    rows: () => this.privilegedIdentityRows(),
  };

  private readonly globalAdminSection: ReportDetailSection = {
    key: 'globalAdministrators',
    titleKey: 'app.reports.dashboard.global_administrators',
    icon: 'admin_panel_settings',
    columns: this.userAssignmentColumns,
    rows: () => this.globalAdminRows(),
  };

  private readonly disabledWithRolesSection: ReportDetailSection = {
    key: 'disabledWithRoles',
    titleKey: 'app.reports.dashboard.disabled_with_roles',
    icon: 'no_accounts',
    columns: this.userAssignmentColumns,
    rows: () => this.disabledWithRolesRows(),
  };

  constructor() {
    this.registerAssignmentTypeDonutEffect();
    this.registerRoleBarEffect();
  }

  openSectionByKey(key: string): void {
    const section = this.findSectionByKey(key);
    if (section) this.openSection(section);
  }

  openPrivilegedIdentitiesDialog(): void {
    this.openSection(this.privilegedIdentitiesSection);
  }

  openGlobalAdminDialog(): void {
    this.openSection(this.globalAdminSection);
  }

  openDisabledWithRolesDialog(): void {
    this.openSection(this.disabledWithRolesSection);
  }

  isFailureStatus(status: string): boolean {
    return status.toLowerCase().includes('fail');
  }

  exportAllCsv(): void {
    exportAllToCsv('entra-admin-roles-report', this.sections.map(section => ({
      title: this.translate.instant(section.titleKey),
      columns: section.columns,
      rows: section.rows(),
    })), key => this.translate.instant(key));
  }

  private findSectionByKey(key: string): ReportDetailSection | undefined {
    return [this.privilegedIdentitiesSection, this.globalAdminSection, this.disabledWithRolesSection, ...this.sections]
      .find(section => section.key === key);
  }

  private openSection(section: ReportDetailSection): void {
    this.dialog.open(ReportDetailDialogComponent, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      panelClass: 'report-detail-dialog-panel',
      data: {
        titleKey: section.titleKey,
        icon: section.icon,
        columns: section.columns,
        rows: section.rows(),
      },
    });
  }

  private registerAssignmentTypeDonutEffect(): void {
    effect(() => {
      this.themeService.mode();
      this.translationsReady();
      this.assignmentTypeDonutOptions.set(this.buildAssignmentTypeDonutOptions());
    });
  }

  private registerRoleBarEffect(): void {
    effect(() => {
      this.themeService.mode();
      this.translationsReady();
      this.roleBarOptions.set(this.buildRoleBarOptions());
    });
  }

  private buildAssignmentTypeDonutOptions(): EChartsCoreOption {
    const palette = readChartThemePalette();
    const data = this.facade.assignmentTypeDonutData();
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      textStyle: { color: palette.text },
      legend: legendBottom(palette),
      color: [CHART_ACCENT_ORANGE, CHART_ACCENT_PURPLE],
      series: [{
        ...donutSeriesDefaults(),
        itemStyle: { borderColor: palette.surface, borderWidth: 2 },
        data: data.map(datum => ({ name: this.translate.instant(datum.name), value: datum.value, key: datum.key })),
      }],
    };
  }

  private buildRoleBarOptions(): EChartsCoreOption {
    const palette = readChartThemePalette();
    const data = [...this.facade.roleBarData()].reverse();
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 8, right: 24, top: 12, bottom: 8, containLabel: true },
      xAxis: integerValueAxis(palette),
      yAxis: categoryAxis(palette, data.map(datum => datum.name)),
      series: [{
        type: 'bar',
        barMaxWidth: 22,
        itemStyle: { color: palette.primary, borderRadius: [0, 4, 4, 0] },
        data: data.map(datum => ({ name: datum.name, value: datum.value })),
      }],
    };
  }
}
